import {
  type ColumnType,
  type Selectable,
  type Insertable,
  type Updateable,
} from 'kysely'
import { z } from 'zod'

import { cableId, type CableId } from './Cable.js'
import {
  deviceInventoryStatusType,
  type default as DeviceInventoryStatusType,
} from './DeviceInventoryStatusType.js'
import { organizationId, type OrganizationId } from './Organization.js'
import { projectId, type ProjectId } from './Project.js'
import { userId, type UserId } from './User.js'

/** Identifier type for public.cable_history_ */
export type CableHistoryId = string & { __brand: 'CableHistoryId' }

/** Represents the table public.cable_history_ */
export default interface CableHistoryTable {
  /** Database type: pg_catalog.uuid */
  id: ColumnType<CableHistoryId, CableHistoryId | undefined, CableHistoryId>

  /** Database type: pg_catalog.timestamptz */
  createdAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  createdBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.timestamptz */
  updatedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  updatedBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.timestamptz */
  deletedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  deletedBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.int4 */
  cableId: ColumnType<CableId, CableId, CableId>

  /** Database type: pg_catalog.timestamptz */
  startedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.timestamptz */
  endedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: public.device_inventory_status_type_ */
  inventoryStatus: ColumnType<
    DeviceInventoryStatusType | null,
    DeviceInventoryStatusType | null,
    DeviceInventoryStatusType | null
  >

  /** Database type: pg_catalog.int4 */
  organizationId: ColumnType<OrganizationId, OrganizationId, OrganizationId>

  /** Database type: pg_catalog.int4 */
  projectId: ColumnType<ProjectId | null, ProjectId | null, ProjectId | null>
}

export type CableHistory = Selectable<CableHistoryTable>

export type CableHistoryInitializer = Insertable<CableHistoryTable>

export type CableHistoryMutator = Updateable<CableHistoryTable>

export const cableHistoryId = z.string() as unknown as z.Schema<CableHistoryId>

export const cableHistory = z.object({
  id: cableHistoryId,
  createdAt: z.coerce.date().nullable().optional(),
  createdBy: userId.nullable().optional(),
  updatedAt: z.coerce.date().nullable().optional(),
  updatedBy: userId.nullable().optional(),
  deletedAt: z.coerce.date().nullable().optional(),
  deletedBy: userId.nullable().optional(),
  cableId: cableId,
  startedAt: z.coerce.date().nullable().optional(),
  endedAt: z.coerce.date().nullable().optional(),
  inventoryStatus: deviceInventoryStatusType.nullable().optional(),
  organizationId: organizationId,
  projectId: projectId.nullable().optional(),
}) as unknown as z.Schema<CableHistory>

export const cableHistoryInitializer = z.object({
  id: cableHistoryId.optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: userId.optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: userId.optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: userId.optional().nullable(),
  cableId: cableId,
  startedAt: z.coerce.date().optional().nullable(),
  endedAt: z.coerce.date().optional().nullable(),
  inventoryStatus: deviceInventoryStatusType.optional().nullable(),
  organizationId: organizationId,
  projectId: projectId.optional().nullable(),
}) as unknown as z.Schema<CableHistoryInitializer>

export const cableHistoryMutator = z.object({
  id: cableHistoryId.optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: userId.optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: userId.optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: userId.optional().nullable(),
  cableId: cableId.optional(),
  startedAt: z.coerce.date().optional().nullable(),
  endedAt: z.coerce.date().optional().nullable(),
  inventoryStatus: deviceInventoryStatusType.optional().nullable(),
  organizationId: organizationId.optional(),
  projectId: projectId.optional().nullable(),
}) as unknown as z.Schema<CableHistoryMutator>
